import React, { useState, useMemo } from 'react';

import { Box, Tabs, Tab, Grid, Typography } from '@mui/material';
import { FundDataBasic } from '../../interfaces/DTO/FundDataBasic';
import { ProductType } from '../../interfaces/DTO/mislaka_xml_models';
import { InvestmentPathTree } from '../PersonFinancialData/Tree/InvestmentPathTree';
import { InvestmentPathTreeByLiquidity } from '../PersonFinancialData/Tree/InvestmentPathTreeByLiquidity';
import { ProductFundYieldsChart } from '../PersonFinancialData/Charts/ProductFundYieldsChart';
import { MonthlyReturnsGrid } from '../PersonFinancialData/Metrics/MonthlyReturnsGrid';
import { MimshakService } from '../../services/mimshakService';
import { MimshakTree } from '../PersonFinancialData/Tree/MimshakTree';
import InvestmentPathTable from '../PersonFinancialData/Tabels/InvestmentPathTable';
import WeightedPerformanceBar from '../PersonFinancialData/Charts/WeightedPerformanceBar';
import { MimshakDataUtil } from '../../services/mimshakDataUtil';
import { fundService } from '../../services/fundService';
import { ExposureGaugesByProduct } from '../PersonFinancialData/ExposureGaugesByProduct';
import { Contact } from '../../interfaces/DTO/crm/contact';
import { LOCAL_STORAGE_KEYS } from '../../services/local_storage_keys';
import WeightedProductYieldsSummary from '../PersonFinancialData/Cards/AverageFundYieldsSummary';
import ScatterFundAmountChart from '../PersonFinancialData/Charts/ScatterFundAmountChart';
import { MimshakData } from '../../interfaces/DTO/mimshakData';
import ProductDistributionCharts from '../PersonFinancialData/Charts/ProductDistributionCharts';


interface ExposurePageProps {
  mimshakData: MimshakData[];
  contacts: Contact[];
  selectedReportedPeriod: string;
  selectedPeriodType: "REGULAR" | "PRE_ADVICE";
  viewedType: "USER" | "FAMILY";
  familyId?: number;
}

const ExposurePage: React.FC<ExposurePageProps> = ({
  mimshakData,
  contacts,
  selectedReportedPeriod,
  selectedPeriodType,
  viewedType,
  familyId
}) => {
  const [selectedPolicyNumbers, setSelectedPolicyNumbers] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState(0);

  const flatMimshaks = useMemo(() => MimshakDataUtil.flatten(mimshakData), [mimshakData]);
  const officialProducts = useMemo(() => MimshakDataUtil.extractAllProducts(mimshakData), [mimshakData]);

  const fundData = useMemo(() => {
    const codes = MimshakService.extractPathCodesFromProducts(officialProducts);
    const uniqueCodes = Array.from(new Set(codes));
    return uniqueCodes
      .map((id) => fundService.getFundFromCacheById(id))
      .filter(Boolean) as FundDataBasic[];
  }, [officialProducts]);

  const filteredProducts = useMemo(() => {
    return MimshakService.filterProductsByPolicyNumber(officialProducts, selectedPolicyNumbers);
  }, [officialProducts, selectedPolicyNumbers]);

  const filteredPolicies = useMemo(() => {
    return filteredProducts.flatMap(p => p.policies_or_accounts);
  }, [filteredProducts]);

  const handleTreeSelect = (selectedItems: string[]) => {
    setSelectedPolicyNumbers(selectedItems);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const liquidFundsTypes = [
    ProductType.EDUCATION_FUND,
    ProductType.PURE_SAVINGS_POLICY,
    ProductType.INVESTMENT_PROVIDENT_FUND,
    ProductType.CHILD_SAVINGS,
    ProductType.PROVIDENT_FUND,
  ];

  const tabs = [
    {
      label: 'חשיפה',
      content: (
        <>
          <ExposureGaugesByProduct products={filteredProducts} fundData={fundData} />
          <Box sx={{ mt: 5, mb: 4 }}>
            <ScatterFundAmountChart
              products={filteredProducts}
              fundData={fundData}
              title="השקעות קיימות - תשואה מול חשיפה למניות"
            />
          </Box>
          <ProductFundYieldsChart products={filteredProducts} />
        </>
      )
    },
    {
      label: 'תשואה פסיבית',
      content: (
        <MonthlyReturnsGrid contacts={contacts} productTypes={liquidFundsTypes} filteredProducts={filteredProducts} />
      )
    },
    {
      label: 'פירוט מסלולי השקעה',
      content: (
        <>
          <WeightedProductYieldsSummary products={filteredProducts} title="תשואה שנתית משוקללת של המסלולים" />
          <WeightedPerformanceBar products={filteredProducts} fundData={fundData} />
          <InvestmentPathTable products={filteredProducts} fundData={fundData} />
        </>
      )
    },
    {
      label: 'פיזור מוצרים',
      content: (
        <>
          <ProductDistributionCharts products={filteredProducts} />
          <Grid container spacing={2}>
          </Grid>
        </>
      )
    }
  ];

  if (!selectedReportedPeriod) {
    return <Typography>טוען נתוני תקופה...</Typography>;
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column' }}>
        <MimshakTree
          mimshaks={flatMimshaks}
          searchResults={selectedPolicyNumbers}
          onSelect={handleTreeSelect}
          storageKey={LOCAL_STORAGE_KEYS.mimshakHierarchyView_Tree_Exposure_tab}
        />
        <InvestmentPathTree products={filteredProducts} fundData={fundData} />
        <InvestmentPathTreeByLiquidity products={filteredProducts} fundData={fundData} contacts={contacts} />
      </Grid>
      <Grid item xs={12} md={8} sx={{ borderRight: '1px solid #e0e0e0', pl: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary" sx={{ mb: 2, mt: 2 }}>
          {tabs.map((tab, index) => (
            <Tab key={index} label={tab.label} sx={{ borderLeft: activeTab === index ? 'lightgray' : '1px solid #EEE', fontSize: '1.25rem' }} />
          ))}
        </Tabs>
        <Box>
          {tabs[activeTab].content}
        </Box>
      </Grid>
    </Grid>
  );
};

export default ExposurePage;
